import t from "@/lang/lang";
import {
  MonitorPestView,
  MonitorView,
} from "@/services/backend/qpm/monitors/types/monitor-view";
import { ReplacePestsReq } from "@/services/backend/qpm/monitors/types/monitor-commands";
import { useQpmMonitorReplacePestsMutation } from "@/services/backend/qpm/monitors/api/monitor-api";
import { PestKind } from "@/services/backend/qpm/pests/types/pest-enum";
import {
  PestSelectorComponent,
  SelectedPestsTable,
} from "./edit-monitor-pest-category";

export function MonitorPestsComponent({
  monitorId,
  monitor,
  readOnly,
}: {
  monitorId: string;
  monitor: MonitorView;
  readOnly?: boolean;
}) {
  const [replace] = useQpmMonitorReplacePestsMutation();

  // Some parts of the codebase still type `monitor.pests` inconsistently.
  // Normalize it here to the shape the replace endpoint expects.
  const pests = ((monitor.pests ?? []) as unknown as MonitorPestView[])
    .map((p) => ({
      id: p.id,
      kind: p.kind,
      name: p.name,
    }))
    .filter((p) => !!p.id);

  const update = (newList: MonitorPestView[]) => {
    if (readOnly) return;
    const payload: ReplacePestsReq = {
      id: monitorId,
      pests: newList.map((p) => ({
        id: p.id,
        kind: p.kind,
        name: p.name,
      })),
    };

    replace(payload);
  };

  const add = (p: MonitorPestView) => {
    if (readOnly) return;
    if (pests.some((x) => x.id === p.id && x.kind === p.kind)) return;
    update([...pests, p]);
  };

  const remove = (kind: PestKind, id: string) => {
    if (readOnly) return;
    update(pests.filter((p) => !(p.kind === kind && p.id === id)));
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="text-sm font-semibold">{t("Schädlinge")}</div>
      <div className="grid items-start gap-6 md:grid-cols-2">
        <div className="flex flex-col gap-2">
          <div className="text-sm font-medium">{t("Kategorien")}</div>
          <PestSelectorComponent
            kind={PestKind.Category}
            selectedItems={pests}
            onSelect={add}
            disabled={readOnly}
          />
          <SelectedPestsTable
            kind={PestKind.Category}
            selectedItems={pests}
            onDeleteSelected={(id) => remove(PestKind.Category, id)}
            disabled={readOnly}
          />
        </div>
        <div className="flex flex-col gap-2">
          <div className="text-sm font-medium">{t("Indikatoren")}</div>
          <PestSelectorComponent
            kind={PestKind.Indicator}
            selectedItems={pests}
            onSelect={add}
            disabled={readOnly}
          />
          <SelectedPestsTable
            kind={PestKind.Indicator}
            selectedItems={pests}
            onDeleteSelected={(id) => remove(PestKind.Indicator, id)}
            disabled={readOnly}
          />
        </div>
      </div>
    </div>
  );
}
